import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, Crown, Users, Trash2, AlertTriangle } from 'lucide-react';
import Avatar from '../../components/Avatar';
import API from '../../api/axios';
import toast from 'react-hot-toast';

const AdminMembers = ({ group, isAdmin, onRefresh }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // member object to delete
  const [deleting, setDeleting] = useState(false);
  const [adminInviteLink, setAdminInviteLink] = useState('');

  useEffect(() => {
    if (isAdmin) loadPayments();
  }, [group._id]);

  const handleCreateAdminInvite = async () => {
    try {
      const { data } = await API.post(`/groups/${group._id}/invite-admin`);
      setAdminInviteLink(data.link);
      await navigator.clipboard.writeText(data.link);
      toast.success('تم نسخ رابط دعوة الأدمن ✅');
    } catch {
      toast.error('فشل إنشاء رابط الدعوة');
    }
  };

  const loadPayments = async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/payments/group/${group._id}/all`);
      setPayments(data.memberPayments || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatus = (userId) => {
    const mp = payments.find(p => p.user?._id === userId);
    if (!mp) return 'none';
    if (mp.allConfirmed && mp.payments.length > 0) return 'confirmed';
    if (mp.hasPending) return 'pending';
    return 'notpaid';
  };

  // ── DELETE HANDLER lives here where group._id is in scope ──────────────────
  const handleDeleteMember = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await API.delete(`/groups/${group._id}/members/${confirmDelete._id}`);
      toast.success(`تم حذف ${confirmDelete.name} ✅`);
      setConfirmDelete(null);
      loadPayments();
      if (onRefresh) onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'فشل حذف العضو');
    } finally {
      setDeleting(false);
    }
  };

  const adminsList = (group.admins || []).map(a => ({ ...a, isAdmin: true }));
  const allMembers = [
    ...adminsList,
    ...(group.members || []).filter(m => !adminsList.some(a => a._id === m._id))
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ fontWeight: 700, fontSize: '1rem' }}>
          <Users size={16} style={{ display: 'inline', marginLeft: '6px' }} />
          الأعضاء ({allMembers.length} / {group.maxMembers})
        </h3>
        {isAdmin && (
          <button className="btn btn-secondary btn-sm" onClick={handleCreateAdminInvite}>
            <Crown size={14} /> دعوة أدمن
          </button>
        )}
      </div>

      {/* Member rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {allMembers.map((member) => {
          const status = getPaymentStatus(member._id);
          return (
            <MemberRow
              key={member._id}
              member={member}
              status={status}
              isAdmin={isAdmin}
              onView={() => setSelectedMember(member)}
              onDelete={() => setConfirmDelete(member)}
            />
          );
        })}
      </div>

      {/* Payment detail modal */}
      {selectedMember && (
        <MemberPaymentModal
          member={selectedMember}
          groupId={group._id}
          onClose={() => { setSelectedMember(null); loadPayments(); }}
          isAdmin={isAdmin}
        />
      )}

      {/* ── Confirm Delete Modal ─────────────────────────────────────────────── */}
      {confirmDelete && (
        <div
          onClick={() => !deleting && setConfirmDelete(null)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: 'var(--bg-surface, #1a1f2e)',
              border: '1px solid var(--border, rgba(255,255,255,0.08))',
              borderRadius: 20,
              padding: '32px 28px',
              maxWidth: 360,
              width: '100%',
              textAlign: 'center',
              direction: 'rtl',
              fontFamily: 'inherit',
              boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            }}
          >
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <AlertTriangle size={28} color="#ef4444" />
            </div>

            <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 800 }}>
              حذف العضو؟
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '0.875rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              هل أنت متأكد إنك عايز تحذف{' '}
              <strong style={{ color: 'var(--text-primary)' }}>{confirmDelete.name}</strong>{' '}
              من المجموعة؟
            </p>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1 }}
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >
                إلغاء
              </button>
              <button
                className="btn btn-danger"
                style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                onClick={handleDeleteMember}
                disabled={deleting}
              >
                {deleting ? <div className="spinner spinner-sm" /> : <Trash2 size={15} />}
                {deleting ? 'جاري الحذف...' : 'نعم، احذف'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── MemberRow: only fires callbacks, no API calls ─────────────────────────────
const MemberRow = ({ member, status, isAdmin, onView, onDelete }) => {
  const statusConfig = {
    confirmed: { icon: <CheckCircle size={16} />, label: 'تم التأكيد', color: 'var(--success)', bg: 'rgba(16,185,129,0.1)' },
    pending:   { icon: <Eye size={16} />,         label: 'في الانتظار', color: 'var(--warning)', bg: 'rgba(245,158,11,0.1)' },
    notpaid:   { icon: <XCircle size={16} />,     label: 'لم يدفع',     color: 'var(--danger)',  bg: 'rgba(239,68,68,0.1)' },
    none:      { icon: null,                       label: 'لا يوجد دفع', color: 'var(--text-muted)', bg: 'transparent' },
  };
  const s = statusConfig[status] || statusConfig.none;

  return (
    <div className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <Avatar src={member.avatar} name={member.name} size="md" />

      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{member.name}</span>
          {member.isAdmin && (
            <span className="badge badge-warning" style={{ fontSize: '0.7rem', padding: '1px 8px' }}>
              <Crown size={10} /> أدمن
            </span>
          )}
        </div>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{member.email}</span>
      </div>

      {/* Payment status — only for regular members */}
      {!member.isAdmin && status !== 'none' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '5px',
          padding: '4px 12px', borderRadius: 'var(--radius-full)',
          background: s.bg, color: s.color, fontSize: '0.78rem', fontWeight: 600,
        }}>
          {s.icon} {s.label}
        </div>
      )}

      {/* Action buttons — only admins can see these, and only on non-admin members */}
      {isAdmin && !member.isAdmin && (
        <>
          <button className="btn btn-secondary btn-sm" onClick={onView} style={{ padding: '6px 12px' }}>
            <Eye size={14} /> عرض
          </button>
          <button className="btn btn-danger btn-sm" onClick={onDelete} style={{ padding: '6px 12px' }}>
            <Trash2 size={14} /> حذف
          </button>
        </>
      )}
    </div>
  );
};

// ── MemberPaymentModal ────────────────────────────────────────────────────────
const MemberPaymentModal = ({ member, groupId, onClose, isAdmin }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadPayments(); }, []);

  const loadPayments = async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/users/${member._id}/group/${groupId}`);
      setPayments(data.payments || []);
    } catch { }
    finally { setLoading(false); }
  };

  const handleConfirm = async (paymentId) => {
    try {
      await API.put(`/payments/${paymentId}/confirm`);
      toast.success('تم تأكيد الدفع ✅');
      loadPayments();
    } catch { toast.error('فشل التأكيد'); }
  };

  const handleReject = async (paymentId) => {
    try {
      await API.put(`/payments/${paymentId}/reject`);
      toast.success('تم رفض الإيصال، طُلب من العضو إعادة الرفع');
      loadPayments();
    } catch { toast.error('فشل الرفض'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: '520px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Avatar src={member.avatar} name={member.name} size="md" />
            <div>
              <h2 className="modal-title">{member.name}</h2>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{member.email}</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div className="loading-screen"><div className="spinner" /></div>
        ) : payments.length === 0 ? (
          <div className="empty-state" style={{ padding: '30px' }}>
            <p>لا يوجد مدفوعات لهذا العضو</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {payments.map(p => (
              <div key={p._id} style={{
                padding: '14px', background: 'var(--bg-elevated)',
                borderRadius: 'var(--radius-md)', border: '1px solid var(--border)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{p.survey?.title}</span>
                  <span className="badge badge-warning">{p.amount} ج.م</span>
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: '10px' }}>
                  الاختيار: <strong style={{ color: 'var(--text-primary)' }}>{p.optionName}</strong>
                </div>

                {p.receiptUploaded && p.receiptImage ? (
                  <div style={{ marginBottom: '10px' }}>
                    <a href={p.receiptImage} target="_blank" rel="noreferrer">
                      <img src={p.receiptImage} alt="إيصال"
                        style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }} />
                    </a>
                  </div>
                ) : (
                  <div style={{ padding: '12px', background: 'var(--bg-card)', borderRadius: 'var(--radius-sm)', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: '10px' }}>
                    لم يرفع الإيصال بعد
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {p.confirmedByAdmin ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--success)', fontSize: '0.82rem', fontWeight: 600 }}>
                      <CheckCircle size={14} /> تم تأكيد الدفع
                    </span>
                  ) : p.receiptUploaded ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--warning)', fontSize: '0.82rem', fontWeight: 600 }}>
                      <Eye size={14} /> في انتظار التأكيد
                    </span>
                  ) : (
                    <span style={{ color: 'var(--danger)', fontSize: '0.82rem', fontWeight: 600 }}>
                      <XCircle size={14} style={{ display: 'inline', marginLeft: '4px' }} /> لم يدفع
                    </span>
                  )}

                  {isAdmin && p.receiptUploaded && !p.confirmedByAdmin && (
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn btn-success btn-sm" onClick={() => handleConfirm(p._id)} style={{ padding: '5px 12px' }}>
                        <CheckCircle size={13} /> تأكيد
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleReject(p._id)} style={{ padding: '5px 12px' }}>
                        <XCircle size={13} /> رفض
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMembers;